import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClient } from "@supabase/supabase-js";
import AddFamilia from './AddFamilia';
import AdminNavBar from '../../components/AdminNavBar';
import { uploadFamiliaThumbnail } from '../../utils/imageUploadUtils';
import './styles.css';
import supabase from '../../utils/supabaseClient';
import PaginationBar from '../../components/PaginationBar';

// Component to show current thumbnails for each familia
function ThumbnailPreview({ familiaId, refreshTrigger }) {
  const [thumbnails, setThumbnails] = useState([]);

  useEffect(() => {
    const fetchThumbnails = async () => {
      try {
        const { data, error } = await supabase
          .from('familia_thumbnail')
          .select('*')
          .eq('familia_id', familiaId);
        
        if (!error) {
          setThumbnails(data || []);
        }
      } catch (error) {
        console.error('Error fetching thumbnails:', error);
      }
    };

    fetchThumbnails();
  }, [familiaId, refreshTrigger]);

  if (thumbnails.length === 0) {
    return <span style={{ fontSize: '12px', color: '#999' }}>Sin imágenes</span>;
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
      <span style={{ fontSize: '12px', color: '#666' }}>
        {thumbnails.length}/3
      </span>
      <div style={{ display: 'flex', gap: '2px' }}>
        {thumbnails.slice(0, 3).map((thumb, idx) => (
          <img 
            key={thumb.id}
            src={thumb.url} 
            alt={`Thumb ${idx + 1}`} 
            style={{
              width: '20px',
              height: '20px',
              objectFit: 'cover',
              borderRadius: '2px',
              border: '1px solid #ddd'
            }}
          />
        ))}
      </div>
    </div>
  );
}

function EditFamilia() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [familias, setFamilias] = useState([]);
  const [showAddFamiliaPopup, setShowAddFamiliaPopup] = useState(false);
  const [editing, setEditing] = useState(null);

  const [form, setForm] = useState({ nombre: '', number: '' });
  const [message, setMessage] = useState('');
  const [authLoading, setAuthLoading] = useState(true);
  const [draggedItem, setDraggedItem] = useState(null);
  const [draggedOverItem, setDraggedOverItem] = useState(null);
  const [existingThumbnails, setExistingThumbnails] = useState([]);
  const [availableThumbnails, setAvailableThumbnails] = useState([]);
  const [selectedThumbnails, setSelectedThumbnails] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [thumbnailRefresh, setThumbnailRefresh] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const imagesPerPage = 6;

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) {
        console.error('Error getting user:', error);
      } else {
        setUser(user);
      }
    } catch (error) {
      console.error('Error checking auth:', error);
    } finally {
      setAuthLoading(false);
    }
  };

  useEffect(() => {
    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN') {
          setUser(session?.user || null);
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
        }
      }
    );

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (user) {
      fetchFamilias();
    }
  }, [user]);

  const fetchFamilias = async () => {
    const { data } = await supabase
      .from('familia')
      .select('*')
      .order('number', { ascending: true, nullsLast: true });
    setFamilias(data || []);
  };

  const fetchExistingThumbnails = async (familiaId) => {
    if (!familiaId) {
      setExistingThumbnails([]);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('familia_thumbnail')
        .select('*')
        .eq('familia_id', familiaId);

      if (error) throw error;
      setExistingThumbnails(data || []);
    } catch (error) {
      console.error('Error fetching thumbnails:', error);
    }
  };

  // Fetch available thumbnails from storage
  useEffect(() => {
    async function fetchAvailableThumbnails() {
      try {
        const { data, error } = await supabase
          .storage
          .from('productsamples')
          .list('thumbnails', { limit: 100 });
        
        if (error) {
          console.error('Error fetching thumbnails:', error);
          return;
        }

        const thumbnailFiles = data
          .filter(f => f.name.match(/\.(jpg|jpeg|png|webp)$/i))
          .map(f => ({
            name: f.name,
            path: supabase.storage.from('productsamples').getPublicUrl(`thumbnails/${f.name}`).data.publicUrl
          }));
        
        setAvailableThumbnails(thumbnailFiles);
      } catch (error) {
        console.error('Error fetching thumbnails:', error);
      }
    }

    fetchAvailableThumbnails();
  }, []);

  // Move familia up in order
  const moveUp = async (familia) => {
    const currentIndex = familias.findIndex(f => f.id === familia.id);
    if (currentIndex <= 0) return;
    
    const targetFamilia = familias[currentIndex - 1];
    const currentNumber = familia.number || 999;
    const targetNumber = targetFamilia.number || 999;
    
    // Swap the numbers
    await supabase.from('familia').update({ number: targetNumber }).eq('id', familia.id);
    await supabase.from('familia').update({ number: currentNumber }).eq('id', targetFamilia.id);
    
    fetchFamilias();
  };

  // Move familia down in order
  const moveDown = async (familia) => {
    const currentIndex = familias.findIndex(f => f.id === familia.id);
    if (currentIndex >= familias.length - 1) return;
    
    const targetFamilia = familias[currentIndex + 1];
    const currentNumber = familia.number || 999;
    const targetNumber = targetFamilia.number || 999;
    
    // Swap the numbers
    await supabase.from('familia').update({ number: targetNumber }).eq('id', familia.id);
    await supabase.from('familia').update({ number: currentNumber }).eq('id', targetFamilia.id);
    
    fetchFamilias();
  };

  // Handle drag start
  const handleDragStart = (e, familia) => {
    setDraggedItem(familia);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.parentNode);
  };

  // Handle drag over
  const handleDragOver = (e, familia) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDraggedOverItem(familia);
  };

  // Handle drop
  const handleDrop = async (e, targetFamilia) => {
    e.preventDefault();
    
    if (!draggedItem || draggedItem.id === targetFamilia.id) {
      setDraggedItem(null);
      setDraggedOverItem(null);
      return;
    }

    const draggedIndex = familias.findIndex(f => f.id === draggedItem.id);
    const targetIndex = familias.findIndex(f => f.id === targetFamilia.id);
    
    // Reorder the array
    const newFamilias = [...familias];
    const [removed] = newFamilias.splice(draggedIndex, 1);
    newFamilias.splice(targetIndex, 0, removed);
    
    // Update numbers in database
    for (let i = 0; i < newFamilias.length; i++) {
      await supabase.from('familia').update({ number: i + 1 }).eq('id', newFamilias[i].id);
    }
    
    setDraggedItem(null);
    setDraggedOverItem(null);
    fetchFamilias();
  };

  // Auto-assign numbers to familias without order
  const autoAssignNumbers = async () => {
    const familiasWithoutNumbers = familias.filter(f => !f.number);
    const maxNumber = Math.max(...familias.map(f => f.number || 0));
    
    for (let i = 0; i < familiasWithoutNumbers.length; i++) {
      await supabase.from('familia')
        .update({ number: maxNumber + i + 1 })
        .eq('id', familiasWithoutNumbers[i].id);
    }
    
    fetchFamilias();
  };

  const toggleThumbnailSelection = (thumbnailPath) => {
    const isSelected = selectedThumbnails.includes(thumbnailPath);
    const totalSelected = selectedThumbnails.length + existingThumbnails.length;
    
    if (!isSelected && totalSelected >= 3) {
      setMessage('Solo se permiten máximo 3 imágenes por familia');
      return;
    }
    
    if (isSelected) {
      setSelectedThumbnails(prev => prev.filter(path => path !== thumbnailPath));
    } else {
      setSelectedThumbnails(prev => [...prev, thumbnailPath]);
    }
  };

  const saveThumbnailSelections = async () => {
    if (!editing || selectedThumbnails.length === 0) return;

    try {
      for (const thumbnailPath of selectedThumbnails) {
        const { error } = await supabase
          .from('familia_thumbnail')
          .insert({
            familia_id: editing,
            url: thumbnailPath
          });

        if (error) throw error;
      }

      setMessage('Imágenes seleccionadas correctamente');
      fetchExistingThumbnails(editing);
      setThumbnailRefresh(prev => prev + 1);
      setSelectedThumbnails([]);
    } catch (error) {
      console.error('Error saving thumbnail selections:', error);
      setMessage('Error al guardar las imágenes seleccionadas');
    }
  };

  // Handle thumbnail upload using utility function
  const handleThumbnailUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (existingThumbnails.length >= 3) {
      setMessage('Ya tienes el máximo de 3 imágenes. Elimina alguna para subir una nueva.');
      return;
    }

    setUploading(true);
    setMessage('');

    try {
      const result = await uploadFamiliaThumbnail(file, editing);
      
      if (result.success) {
        setMessage(result.message);
        fetchExistingThumbnails(editing);
        setThumbnailRefresh(prev => prev + 1);
        
        // Clear file input
        e.target.value = '';
      } else {
        setMessage(result.error);
      }
      
    } catch (error) {
      console.error('Error uploading thumbnail:', error);
      setMessage('Error inesperado al subir la imagen');
    } finally {
      setUploading(false);
    }
  };

  const deleteThumbnail = async (thumbnailUrl) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('¿Estás seguro de que quieres eliminar esta imagen?')) return;

    try {
      
      // Try using the service role (bypasses RLS) - TEMPORARY FOR TESTING
      const { data, error } = await supabase
        .from('familia_thumbnail')
        .delete()
        .eq('familia_id', editing)
        .eq('url', thumbnailUrl);
      if (error) {
        console.error('Supabase error details:', error);
        // Try to give more specific error message
        if (error.message.includes('policy')) {
          setMessage('Error: No tienes permisos para eliminar esta imagen. Contacta al administrador.');
          return;
        }
        throw error;
      }

      setMessage('Imagen eliminada correctamente');
      fetchExistingThumbnails(editing);
      setThumbnailRefresh(prev => prev + 1);
    } catch (error) {
      console.error('Error deleting thumbnail:', error);
      setMessage('Error al eliminar la imagen');
    }
  };

  const clearSelections = () => {
    setSelectedThumbnails([]);
  };

  const getThumbnailsToShow = () => {
    const startIndex = (currentPage - 1) * imagesPerPage;
    return availableThumbnails.slice(startIndex, startIndex + imagesPerPage);
  };

  const totalPages = Math.ceil(availableThumbnails.length / imagesPerPage);

  async function handleSave(e) {
    e.preventDefault();
    
    // Check authentication
    if (!user) {
      setMessage('Debes iniciar sesión para editar familias');
      return;
    }
    
    setMessage('');
    const updateData = { nombre: form.nombre };
    
    // Only include number if it's provided
    if (form.number !== '') {
      updateData.number = parseInt(form.number);
    }
    
    const { error } = await supabase.from('familia').update(updateData).eq('id', editing);
    if (error) {
      setMessage('Error al actualizar familia');
    } else {
      setMessage('Familia actualizada');
      setEditing(null);
      setForm({ nombre: '', number: '' });
      fetchFamilias();
    }
  }

  async function handleDelete(id) {
    // Check authentication
    if (!user) {
      setMessage('Debes iniciar sesión para eliminar familias');
      return;
    }
    
    const confirmDelete = window.confirm('¿Está seguro de que desea eliminar esta familia?');
    if (!confirmDelete) return;

    const { error } = await supabase.from('familia').delete().eq('id', id);
    if (!error) {
      setFamilias(familias.filter(f => f.id !== id));
    } else {
      setMessage('Error al eliminar familia');
    }
  }

  if (authLoading) {
    return <div className="loading">Cargando...</div>;
  }

  // If user is not authenticated, show login message
  if (!user) {
    return (
      <>
        <AdminNavBar user={user} />
        <div className="page-container">
          <div className="auth-message">
            <h2>Acceso Restringido</h2>
            <p>Debes iniciar sesión para acceder a la gestión de familias.</p>
            <button 
              onClick={() => navigate('/admin')}
              className="primary-button"
            >
              Ir a Iniciar Sesión
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <AdminNavBar user={user} />
      <div className="page-container">
        <div className="page-header">
          <h2>Editar Familia</h2>
          <div className="header-actions">
            {user && (
              <span className="user-info">
                Conectado como: {user.email}
              </span>
            )}
            <button
              onClick={autoAssignNumbers}
              className="secondary-button"
              style={{ marginRight: '10px' }}
            >
              Auto-ordenar
            </button>
            <button
              onClick={() => setShowAddFamiliaPopup(true)}
              className="primary-button"
            >
              Agregar Familia
            </button>
          </div>
        </div>

        <div style={{ marginBottom: '20px', padding: '10px', background: '#f9f9f9', borderRadius: '5px' }}>
          <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
            💡 <strong>Tip:</strong> Arrastra desde la columna 🔄 para reordenar, usa las flechas ↑↓ para mover una posición, 
            o haz clic en "Auto-ordenar" para asignar números automáticamente.
          </p>
        </div>

        {showAddFamiliaPopup && (
          <div className="popup">
            <div className="popup-content">
              <button
                onClick={() => setShowAddFamiliaPopup(false)}
                className="popup-close"
                title="Cerrar"
              >
                ×
              </button>
              <div>
                <AddFamilia 
                  onClose={() => setShowAddFamiliaPopup(false)} 
                  familias={familias}
                />
              </div>
            </div>
          </div>
        )}

        <table className="data-table">
          <thead>
            <tr>
              <th>🔄</th>
              <th>ID</th>
              <th>Nombre</th>
              <th>Orden</th>
              <th>Thumbnails</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {familias.map((familia, index) => (
              <tr 
                key={familia.id}
                style={{
                  backgroundColor: draggedOverItem?.id === familia.id ? '#e3f2fd' : 'transparent',
                  opacity: draggedItem?.id === familia.id ? 0.5 : 1
                }}
              >
                <td 
                  style={{ 
                    textAlign: 'center', 
                    padding: '8px',
                    cursor: 'move',
                    userSelect: 'none'
                  }}
                  draggable
                  onDragStart={(e) => handleDragStart(e, familia)}
                  onDragOver={(e) => handleDragOver(e, familia)}
                  onDrop={(e) => handleDrop(e, familia)}
                  onDragLeave={() => setDraggedOverItem(null)}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                    <button
                      onClick={() => moveUp(familia)}
                      disabled={index === 0}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: index === 0 ? 'not-allowed' : 'pointer',
                        fontSize: '12px',
                        color: index === 0 ? '#ccc' : '#666',
                        padding: '2px'
                      }}
                      title="Mover arriba"
                    >
                      ↑
                    </button>
                    <span style={{ fontSize: '12px', color: '#999' }}>⋮⋮</span>
                    <button
                      onClick={() => moveDown(familia)}
                      disabled={index === familias.length - 1}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: index === familias.length - 1 ? 'not-allowed' : 'pointer',
                        fontSize: '12px',
                        color: index === familias.length - 1 ? '#ccc' : '#666',
                        padding: '2px'
                      }}
                      title="Mover abajo"
                    >
                      ↓
                    </button>
                  </div>
                </td>
                <td>{familia.id}</td>
                <td>{familia.nombre}</td>
                <td>
                  <span style={{ 
                    display: 'inline-block',
                    background: familia.number ? '#e8f5e8' : '#fff3cd',
                    padding: '2px 6px',
                    borderRadius: '3px',
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}>
                    {familia.number || 'Sin orden'}
                  </span>
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {/* Show current thumbnails */}
                    <ThumbnailPreview familiaId={familia.id} refreshTrigger={thumbnailRefresh} />
                  </div>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: '5px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => {
                        setEditing(familia.id);
                        setForm({ 
                          nombre: familia.nombre, 
                          number: familia.number || '' 
                        });
                        fetchExistingThumbnails(familia.id);
                        setSelectedThumbnails([]);
                        setCurrentPage(1);
                      }}
                      className="secondary-button"
                      style={{ fontSize: '11px', padding: '4px 6px' }}
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => handleDelete(familia.id)}
                      className="danger-button"
                      style={{ fontSize: '11px', padding: '4px 6px' }}
                    >
                      Eliminar
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Edit Form Popup - Includes thumbnail management */}
        {editing && form.nombre && (
          <div className="popup">
            <div className="popup-content" style={{ width: '90%', maxWidth: '800px' }}>
              <button
                onClick={() => {
                  setEditing(null);
                  setForm({ nombre: '', number: '' });
                  setExistingThumbnails([]);
                  setSelectedThumbnails([]);
                }}
                className="popup-close"
                title="Cerrar"
              >
                ×
              </button>
              <form onSubmit={handleSave}>
                <h3>Editar Familia</h3>
                
                {/* Basic Info Section */}
                <div style={{ marginBottom: '20px' }}>
                  <label>
                    Nombre:
                    <input
                      value={form.nombre}
                      onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
                      required
                      style={{ width: '100%', padding: '8px', margin: '5px 0' }}
                    />
                  </label>
                  <label>
                    Orden (número):
                    <input
                      type="number"
                      value={form.number}
                      onChange={e => setForm(f => ({ ...f, number: e.target.value }))}
                      placeholder="Ingresa un número para ordenar"
                      min="1"
                      style={{ width: '100%', padding: '8px', margin: '5px 0' }}
                    />
                  </label>
                  <small style={{ color: '#666', marginBottom: '15px', display: 'block' }}>
                    El orden determina cómo aparecen las familias en los filtros. 
                    Números más bajos aparecen primero.
                  </small>
                </div>

                {/* Thumbnail Management Section */}
                <div style={{ marginBottom: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #ddd' }}>
                  <h4 style={{ margin: '0 0 15px 0', color: '#333' }}>Gestionar Imágenes (máximo 3)</h4>
                  
                  {/* Current Thumbnails */}
                  {existingThumbnails.length > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                      <h5 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#666' }}>Imágenes actuales ({existingThumbnails.length}/3):</h5>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 15, padding: '10px', background: 'white', borderRadius: '5px', border: '1px solid #ddd' }}>
                        {existingThumbnails.map((thumbnail) => (
                          <div key={thumbnail.id} style={{ position: 'relative' }}>
                            <img 
                              src={thumbnail.url} 
                              alt="Thumbnail" 
                              style={{
                                width: '120px',
                                height: '120px',
                                objectFit: 'cover',
                                borderRadius: '5px',
                                border: '2px solid #ddd'
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => deleteThumbnail(thumbnail.url)}
                              style={{
                                position: 'absolute',
                                top: '-5px',
                                right: '-5px',
                                background: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '50%',
                                width: '20px',
                                height: '20px',
                                fontSize: '12px',
                                cursor: 'pointer'
                              }}
                              title="Eliminar imagen"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Select New Thumbnails */}
                  <div>
                    <h5 style={{ margin: '0 0 15px 0', fontSize: '14px', color: '#666' }}>
                      Seleccionar nuevas imágenes ({selectedThumbnails.length} seleccionadas):
                    </h5>
                    
                    {existingThumbnails.length >= 3 ? (
                      <div style={{ textAlign: 'center', padding: '20px', background: 'white', borderRadius: '5px', border: '1px solid #ddd' }}>
                        <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                          Ya tienes 3 imágenes (máximo permitido). Elimina alguna para seleccionar nuevas.
                        </p>
                      </div>
                    ) : (
                      <>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 15, padding: '10px', background: 'white', borderRadius: '5px', border: '1px solid #ddd', maxHeight: '200px', overflowY: 'auto' }}>
                          {getThumbnailsToShow().map(img => {
                            const canSelect = existingThumbnails.length + selectedThumbnails.length < 3;
                            const isSelected = selectedThumbnails.includes(img.path);
                            
                            return (
                              <div
                                key={img.path}
                                onClick={() => {
                                  if (canSelect || isSelected) {
                                    toggleThumbnailSelection(img.path);
                                  }
                                }}
                                style={{
                                  position: 'relative',
                                  cursor: (canSelect || isSelected) ? 'pointer' : 'not-allowed',
                                  opacity: (canSelect || isSelected) ? 1 : 0.5
                                }}
                              >
                                <img
                                  src={img.path}
                                  alt={img.name}
                                  title={img.name}
                                  style={{
                                    width: '100px',
                                    height: '100px',
                                    objectFit: 'cover',
                                    border: isSelected ? '3px solid #007bff' : '2px solid #ddd',
                                    borderRadius: '5px',
                                    transition: 'border-color 0.2s'
                                  }}
                                />
                                {isSelected && (
                                  <div style={{
                                    position: 'absolute',
                                    top: '2px',
                                    right: '2px',
                                    background: '#007bff',
                                    color: 'white',
                                    borderRadius: '50%',
                                    width: '20px',
                                    height: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '12px'
                                  }}>
                                    ✓
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        
                        {/* Pagination */}
                        <PaginationBar
                          currentPage={currentPage}
                          totalItems={availableThumbnails.length}
                          itemsPerPage={imagesPerPage}
                          onPageChange={page => setCurrentPage(page)}
                          showInfo={false}
                          label="thumbnails"
                        />
                        
                        {/* Selection Actions */}
                        {selectedThumbnails.length > 0 && (
                          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '10px' }}>
                            <button
                              type="button"
                              onClick={saveThumbnailSelections}
                              className="primary-button"
                              style={{ fontSize: '12px', padding: '6px 12px' }}
                            >
                              Guardar Selección ({selectedThumbnails.length})
                            </button>
                            <button
                              type="button"
                              onClick={clearSelections}
                              className="secondary-button"
                              style={{ fontSize: '12px', padding: '6px 12px' }}
                            >
                              Limpiar
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  
                  {/* Upload New Thumbnail */}
                  {existingThumbnails.length < 3 && (
                    <div style={{ marginTop: '20px' }}>
                      <h5 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#666' }}>Subir nueva imagen:</h5>
                      <label
                        htmlFor="thumbnail-upload-familia"
                        className="primary-button"
                        style={{ 
                          display: 'inline-block',
                          padding: '8px 16px',
                          cursor: uploading ? 'not-allowed' : 'pointer',
                          opacity: uploading ? 0.7 : 1
                        }}
                      >
                        {uploading ? 'Subiendo...' : 'Elegir archivo'}
                        <input
                          id="thumbnail-upload-familia"
                          type="file"
                          accept="image/*"
                          onChange={handleThumbnailUpload}
                          disabled={uploading}
                          style={{ display: 'none' }}
                        />
                      </label>
                      <p style={{ margin: '5px 0 0 0', fontSize: '11px', color: '#999' }}>
                        La imagen será comprimida automáticamente (máx. 1MB, 512px)
                      </p>
                    </div>
                  )}
                  
                  {existingThumbnails.length >= 3 && (
                    <p style={{ margin: 0, fontSize: '12px', color: '#666', textAlign: 'center', padding: '10px' }}>
                      Límite de 3 imágenes alcanzado. Elimina alguna para seleccionar nuevas.
                    </p>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button 
                    type="button" 
                    onClick={() => {
                      setEditing(null);
                      setForm({ nombre: '', number: '' });
                      setExistingThumbnails([]);
                      setSelectedThumbnails([]);
                    }}
                    className="secondary-button"
                  >
                    Cancelar
                  </button>
                  <button type="submit" className="primary-button">
                    Guardar
                  </button>
                </div>
                {message && (
                  <div className={message.includes('actualizada') || message.includes('correctamente') ? 'success-message' : 'error-message'} style={{ marginTop: '10px' }}>
                    {message}
                  </div>
                )}
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default EditFamilia;